import React from "react";
import { Alert, Collapse, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { CustomeTextField } from "../core/CustomeTextField";
import { IContract } from "../../interfaces/IContract";
import { IClient } from "../../interfaces/IClient";
import { IClientType } from "../../interfaces/IClientType";
import { getIsAdmin } from "../core/helper";
import { RELEASE_STAGES } from "../../interfaces/releaseStages";
import { ISmtpProfile } from "../../interfaces/ISmtpProfile";


interface IProps {
    editClient: IClient;
    setEditClient: Function;
    clientTypeArray: IClientType[];
    contractArray: IContract[];
    smtpProfileArray: ISmtpProfile[];
}



export const ClientEdit:React.FC<IProps> = ({editClient,setEditClient, clientTypeArray, contractArray, smtpProfileArray}) => {
    const isAdmin = getIsAdmin();
    const smtpProfile = smtpProfileArray.find(x => x.id === editClient.SmtpProfile);
    const usesSmtpProfile = editClient.SmtpProfile !== null && editClient.SmtpProfile !== undefined;


    return(
        <>
            <Collapse in={editClient.is_locked}>
                <Alert severity="warning">
                    Sie haben die Client-Sperre aktiviert. 
                    <br />Der Client wird {(editClient.locked_at === null) ? <>sofort</> : <>ab dem {new Date(editClient.locked_at).toDateString()}</>} deaktiviert sein.
                </Alert>
            </Collapse>


            <Grid container spacing={2} sx={{mt: 2}}>
                <Grid item sm={12}>
                    <CustomeTextField
                        label="Vertrag"
                        attr="Contract"
                        type="select"
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={!isAdmin}
                    >
                        {contractArray.map(x =>
                            <MenuItem key={`select-contract-${x.id}`} value={x.id}>
                                {x.contract_title}
                            </MenuItem>
                        )}
                    </CustomeTextField>
                </Grid>

                <Grid item sm={6}>
                    <CustomeTextField
                        label="Clienttype"
                        attr="ClientType"
                        type="select"
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={!isAdmin}
                    >
                        {clientTypeArray.map(x =>
                            <MenuItem key={`select-clienttype-${x.id}`} value={x.id}>
                                {x.client_type}
                            </MenuItem>
                        )}
                    </CustomeTextField>
                </Grid>


                <Grid item sm={6}>
                    <CustomeTextField
                        label="Release-Stufe"
                        attr="release_stage"
                        type="select"
                        selectString
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={!isAdmin}
                    >
                        {RELEASE_STAGES.map(x =>
                            <MenuItem key={`release_stage-${x.value}`} value={x.value}>
                                {x.title}
                            </MenuItem>
                        )}
                    </CustomeTextField>
                </Grid>

                <Grid item sm={3}>
                    <CustomeTextField
                        label="Testing (Demo-Instanz)"
                        attr="is_testing"
                        type="boolean"
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={!isAdmin}
                    />
                </Grid>

                <Grid item sm={3}>
                    <CustomeTextField
                        label="Registriert"
                        attr="is_registered"
                        type="boolean"
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={editClient.id < 0 || !isAdmin}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{mt: 2}}>
                <Grid item sm={3}>
                    <CustomeTextField
                        label="Sperren"
                        attr="is_locked"
                        type="boolean"
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={editClient.id < 0 || !isAdmin}
                    />
                </Grid>
                <Grid item sm={9}>
                    <CustomeTextField
                        label="Sperren am (leer, sofort)"
                        attr="locked_at"
                        type="date"
                        object={editClient}
                        setObject={setEditClient}
                        disabled={!editClient.is_locked}
                        null
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{mt: 2}}>
                <Grid item sm={6}>
                    <TextField
                        fullWidth
                        label="IP"
                        value={editClient.ip}
                        disabled
                        size="small"
                    />
                </Grid>
                <Grid item sm={6}>
                    <TextField
                        fullWidth
                        label="Servername"
                        value={editClient.servername}
                        disabled
                        size="small"
                    />
                </Grid>

                <Grid item sm={12}>
                    <TextField
                        fullWidth
                        label="FQDN"
                        value={editClient.servername_fqdn}
                        disabled
                        size="small"
                    />
                </Grid>

                <Grid item sm={12}>
                    <TextField
                        fullWidth
                        label="Lizenz (wird generiert)"
                        value={editClient.license_key}
                        rows={2}
                        multiline
                        disabled
                    />
                </Grid>
            </Grid>


            <Typography sx={{mt: 2, mb: 1}}>DiNo Verbindung</Typography>
            <Grid container spacing={2}>
                <Grid item sm={12}>
                    <CustomeTextField
                        label="Öffentlicher Server"
                        attr="public_server"
                        type="string"
                        object={editClient}
                        setObject={setEditClient}
                        required
                        disabled={!isAdmin}
                    />
                </Grid>
            </Grid>


            <Typography sx={{mt: 2, mb: 1}}>SMTP-Server</Typography>
            <Grid container spacing={2}>
                <Grid item sm={12}>
                    {isAdmin
                        ?
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="SMTP-Zugang"
                                value={editClient.SmtpProfile ?? ""}
                                onChange={(e) => setEditClient({
                                    ...editClient,
                                    SmtpProfile: e.target.value === "" ? null : Number(e.target.value),
                                })}
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value=""><i>Eigene Zugangsdaten (nur diese Instanz)</i></MenuItem>
                                {smtpProfileArray.map(x =>
                                    <MenuItem key={`select-smtpprofile-${x.id}`} value={x.id}>
                                        Nr. {x.id} · {x.name} · {x.tls ? "TLS" : "ohne TLS"}
                                    </MenuItem>
                                )}
                            </TextField>
                        :
                            <TextField
                                fullWidth
                                size="small"
                                label="SMTP-Zugang"
                                value={usesSmtpProfile ? "Zentral verwaltet" : "Eigene Zugangsdaten"}
                                disabled
                            />
                    }
                </Grid>
            </Grid>

            <Collapse in={usesSmtpProfile && smtpProfile !== undefined}>
                <Alert severity="info" sx={{mt: 2}}>
                    Versand über {smtpProfile?.email} ({smtpProfile?.server}:{smtpProfile?.port}{smtpProfile?.tls ? ", TLS" : ""}).
                    Zugangsdaten werden zentral unter „SMTP-Profile“ gepflegt.
                </Alert>
            </Collapse>

            <Collapse in={!usesSmtpProfile}>
            <Grid container spacing={2} sx={{mt: 0}}>
                <Grid item sm={6}>
                    <CustomeTextField
                        label="Email"
                        attr="email"
                        type="email"
                        object={editClient}
                        setObject={setEditClient}
                        required={!usesSmtpProfile}
                        disabled={!isAdmin}
                    />
                </Grid>
                <Grid item sm={6}>
                    <CustomeTextField
                        label="Passwort"
                        attr="password"
                        type="password"
                        object={editClient}
                        setObject={setEditClient}
                        required={!usesSmtpProfile}
                        disabled={!isAdmin}
                    />
                </Grid>

                <Grid item sm={6}>
                    <CustomeTextField
                        label="SMTP-Server"
                        attr="server"
                        type="string"
                        object={editClient}
                        setObject={setEditClient}
                        required={!usesSmtpProfile}
                        disabled={!isAdmin}
                    />
                </Grid>
                <Grid item sm={4}>
                    <CustomeTextField
                        label="SMTP-Port"
                        attr="port"
                        type="string"
                        object={editClient}
                        setObject={setEditClient}
                        required={!usesSmtpProfile}
                        disabled={!isAdmin}
                    />
                </Grid>
                <Grid item sm={2}>
                    <CustomeTextField
                        label="TLS"
                        attr="tls"
                        type="boolean"
                        object={editClient}
                        setObject={setEditClient}
                        required={!usesSmtpProfile}
                        disabled={!isAdmin}
                    />
                </Grid>
            </Grid>
            </Collapse>

        </>
    )
}